// Download the carrier/broker enrollment forms attached to a plan, filled with
// this employee's data. Server-generated (see server/routes/enrollmentFormRoutes),
// so it works for enrollments a broker entered on the member's behalf — the
// member never had to go through the signing flow.
//
// Renders nothing when the plan has no forms attached.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

export interface EnrollmentFormSummary {
  planId: number;
  planName: string;
  source: "carrier" | "broker";
  formId: number;
  formName: string;
  ready: boolean;
  notReadyReason?: string;
  fieldCount: number;
  mappedFieldCount: number;
}

export function useEnrollmentForms(employeeId?: number) {
  return useQuery<{ hasSignature: boolean; forms: EnrollmentFormSummary[] }>({
    queryKey: ["/api/employees", employeeId, "enrollment-forms"],
    enabled: !!employeeId,
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/enrollment-forms`);
      if (!res.ok) throw new Error("Failed to load enrollment forms");
      return res.json();
    },
  });
}

export function EnrollmentFormDownload({
  employeeId,
  planId,
}: {
  employeeId?: number;
  planId?: number;
}) {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const { data } = useEnrollmentForms(employeeId);

  const forms = (data?.forms ?? []).filter((f) => f.planId === planId);
  if (!employeeId || !planId || forms.length === 0) return null;

  const download = async (form: EnrollmentFormSummary) => {
    if (!form.ready) {
      toast({
        title: "Form not ready",
        description: form.notReadyReason ?? "This form hasn't been prepared yet.",
        variant: "destructive",
      });
      return;
    }
    setDownloading(true);
    try {
      const res = await fetch(
        `/api/employees/${employeeId}/enrollment-forms/${form.source}/${form.formId}/pdf?planId=${form.planId}`,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "Failed to generate form");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${form.formName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (data && !data.hasSignature) {
        toast({
          title: "Form downloaded",
          description: "No signature is on file for this employee, so the signature fields are blank.",
        });
      }
    } catch (error) {
      toast({
        title: "Couldn't generate form",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  const icon = downloading ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin" />
  ) : (
    <FileText className="h-3.5 w-3.5" />
  );

  if (forms.length === 1) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={downloading || !forms[0].ready}
        onClick={() => download(forms[0])}
        title={forms[0].ready ? forms[0].formName : forms[0].notReadyReason}
      >
        {icon}
        {forms[0].ready ? "Enrollment form" : "Form not ready"}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" disabled={downloading}>
          {icon}
          Enrollment forms ({forms.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {forms.map((form) => (
          <DropdownMenuItem
            key={`${form.source}-${form.formId}`}
            onClick={() => download(form)}
            disabled={!form.ready}
            title={form.ready ? undefined : form.notReadyReason}
          >
            <Download className="h-3.5 w-3.5 mr-2" />
            {form.formName}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
